import { isLeft } from 'fp-ts/Either';
import { delay } from 'es-toolkit/promise';
import { pipe } from 'fp-ts/lib/function';
import { chainW, type TaskEither, tryCatch } from 'fp-ts/lib/TaskEither';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MIN_PREVIEW_IMAGES } from '../../../../constants/form';
import {
  usePreviewFileHandler,
  useSingleFileHandler,
} from '../../../../hooks/useDappFileHandlers';
import {
  normalizeAvailableCountries,
  normalizeCountryCodeInputs,
} from '../../../../lib/countries';
import { useTRPC } from '../../../../hooks/useTRPC';
import { resolveToAndroidLocale } from '../../../../lib/android-languages';
import type { UpdateDappFormData } from '../../../../lib/schemas/formSchemas';
import type { TypedError } from '../../../../lib/typedError';
import type { DashboardPreviewData } from '../../../../types/dappForm';
import { captureAndReportError } from '../../../../utils/errorReporting';
import { uploadPortalMediaFile } from '../../../../utils/portalMediaUpload';
import type {
  DashboardDetailsInputChangeEvent,
  FormState,
  PreviewData,
} from '../types';
import {
  applyTextInputLimit,
  createEmptyFormState,
  createEmptyPreviewData,
  hasFormChanges,
} from '../utils/formState';
import {
  buildUpdateDappPayload,
  resolvePresignedUrl,
  resolvePresignedUrls,
} from '../utils/updatePayload';

interface UseDashboardDetailsFormOptions {
  dappId?: string;
}

export function useDashboardDetailsForm({
  dappId,
}: UseDashboardDetailsFormOptions) {
  const trpc = useTRPC();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<FormState>(() =>
    createEmptyFormState(),
  );
  const [initialData, setInitialData] = useState<FormState | null>(null);
  const [dappIconUrl, setDappIconUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [dappPreviewUrls, setDappPreviewUrls] = useState<(string | null)[]>(
    () => Array(MIN_PREVIEW_IMAGES).fill(null),
  );
  const [editorsChoiceGraphicUrl, setEditorsChoiceGraphicUrl] = useState<
    string | null
  >(null);
  const [presignedUrls, setPresignedUrls] = useState<Record<string, string>>(
    {},
  );
  const [previewData, setPreviewData] = useState<PreviewData>(() =>
    createEmptyPreviewData(),
  );

  useEffect(() => {
    setFormData(createEmptyFormState());
    setInitialData(null);
    setDappIconUrl(null);
    setBannerUrl(null);
    setDappPreviewUrls(Array(MIN_PREVIEW_IMAGES).fill(null));
    setEditorsChoiceGraphicUrl(null);
    setPresignedUrls({});
    setIsLoading(true);
    setIsSubmitting(false);
    setIsFormValid(false);
    setHasChanges(false);
    setPreviewData(createEmptyPreviewData());
  }, [dappId]);

  useEffect(() => {
    const fetchDapp = async () => {
      if (!dappId) {
        captureAndReportError('No dapp ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const result = await trpc.getDapp.query({ dappId });

        if (isLeft(result)) {
          captureAndReportError(result.left.message);
          toast.error('Failed to load dapp', {
            description: result.left.message,
          });
          setIsLoading(false);
          return;
        }

        const dapp = result.right;
        setPresignedUrls(dapp.presignedUrls ?? {});

        console.log('[DashboardDetails] Loading dapp data:', {
          dappId,
          rawLanguages: dapp.languages,
          rawAvailableCountries: dapp.availableCountries,
          fallbackLanguages: ['en-US'],
          timestamp: new Date().toISOString(),
        });

        const resolvedLanguages = dapp.languages
          ? dapp.languages.map((lang) => {
              const resolved = resolveToAndroidLocale(lang);
              console.log('[DashboardDetails] Resolving loaded language:', {
                input: lang,
                output: resolved,
              });
              return resolved || lang;
            })
          : ['en-US'];

        const previewCount = Math.max(
          MIN_PREVIEW_IMAGES,
          dapp.dappPreviewUrls?.length || 0,
        );
        const availableCountriesMode =
          dapp.availableCountriesMode === 'exclude' ? 'exclude' : 'include';
        const resolvedAvailableCountries =
          availableCountriesMode === 'exclude'
            ? normalizeCountryCodeInputs(dapp.availableCountries)
            : normalizeAvailableCountries(dapp.availableCountries);
        const loadedData: FormState = {
          dappName: dapp.dappName,
          packageName: dapp.packageName || '',
          subtitle: dapp.subtitle || '',
          description: dapp.description,
          dappIcon: null,
          banner: null,
          dappPreviews: Array(previewCount).fill(null),
          editorsChoiceHeadline: dapp.editorsChoiceHeadline || '',
          editorsChoiceTag: dapp.editorsChoiceTag || '',
          editorsChoiceGraphic: null,
          languages: resolvedLanguages,
          availableCountries: resolvedAvailableCountries,
          availableCountriesMode,
          appWebsite: dapp.appWebsite || '',
          contactEmail: dapp.contactEmail || '',
          supportEmail: dapp.supportEmail,
          tokenDistribution: dapp.tokenDistribution,
          walletAddress: dapp.walletAddress || '',
          licenseUrl: dapp.licenseUrl || '',
          privacyPolicyUrl: dapp.privacyPolicyUrl || '',
        };

        console.log('[DashboardDetails] Loaded form data:', {
          loadedLanguages: loadedData.languages,
          timestamp: new Date().toISOString(),
        });

        setFormData(loadedData);
        setInitialData(loadedData);

        if (dapp.dappIconUrl) {
          setDappIconUrl(dapp.dappIconUrl);
        }
        if (dapp.bannerUrl) {
          setBannerUrl(dapp.bannerUrl);
        }
        if (dapp.dappPreviewUrls && dapp.dappPreviewUrls.length > 0) {
          setDappPreviewUrls([...dapp.dappPreviewUrls]);
        } else {
          setDappPreviewUrls(Array(MIN_PREVIEW_IMAGES).fill(null));
        }
        if (dapp.editorsChoiceGraphicUrl) {
          setEditorsChoiceGraphicUrl(dapp.editorsChoiceGraphicUrl);
        }

        setPreviewData({
          dappName: dapp.dappName,
          subtitle: dapp.subtitle || '',
          description: dapp.description,
          dappIcon: null,
          banner: null,
          dappPreviews: Array(previewCount).fill(null),
          tokenDistribution: dapp.tokenDistribution,
          existingDappIconUrl: dapp.dappIconUrl || null,
          existingBannerUrl: dapp.bannerUrl || null,
          existingPreviewUrls: dapp.dappPreviewUrls
            ? [...dapp.dappPreviewUrls]
            : Array(previewCount).fill(null),
          existingEditorsChoiceGraphicUrl: dapp.editorsChoiceGraphicUrl || null,
        });

        setIsLoading(false);
      } catch (error) {
        captureAndReportError('Failed to load dapp data:', error);
        toast.error('Failed to load dapp data');
        setIsLoading(false);
      }
    };

    void fetchDapp();
  }, [dappId, trpc]);

  const handleInputChange = (event: DashboardDetailsInputChangeEvent) => {
    const { name, value } = event.target;
    const type = 'type' in event.target ? event.target.type : undefined;

    if (
      (name === 'languages' || name === 'availableCountries') &&
      Array.isArray(value)
    ) {
      console.log('[DashboardDetails] Language selection changed:', {
        previousValue:
          name === 'languages'
            ? formData.languages
            : formData.availableCountries,
        newValue: value,
        timestamp: new Date().toISOString(),
      });
      setFormData((previous) => ({
        ...previous,
        [name]: value as string[],
      }));
      return;
    }

    if (type === 'checkbox') {
      const { checked } = event.target as HTMLInputElement;
      setFormData((previous) => ({ ...previous, [name]: checked }));

      if (name === 'tokenDistribution') {
        setPreviewData((previous) => ({
          ...previous,
          tokenDistribution: checked,
        }));
      }

      return;
    }

    const limitedValue = applyTextInputLimit(name, value as string);

    setFormData((previous) => ({
      ...previous,
      [name]: limitedValue,
    }));

    if (name === 'dappName' || name === 'subtitle' || name === 'description') {
      setPreviewData((previous) => ({
        ...previous,
        [name]: limitedValue,
      }));
    }
  };

  useEffect(() => {
    setPreviewData((previous) => ({
      ...previous,
      dappIcon: formData.dappIcon,
      banner: formData.banner,
      dappPreviews: formData.dappPreviews,
      tokenDistribution: formData.tokenDistribution,
      existingDappIconUrl: formData.dappIcon ? null : dappIconUrl,
      existingBannerUrl: formData.banner ? null : bannerUrl,
      existingPreviewUrls: formData.dappPreviews.map((file, index) =>
        file ? null : dappPreviewUrls[index],
      ),
      existingEditorsChoiceGraphicUrl: formData.editorsChoiceGraphic
        ? null
        : editorsChoiceGraphicUrl,
    }));
  }, [
    formData.dappIcon,
    formData.banner,
    formData.dappPreviews,
    formData.editorsChoiceGraphic,
    formData.tokenDistribution,
    dappIconUrl,
    bannerUrl,
    dappPreviewUrls,
    editorsChoiceGraphicUrl,
  ]);

  useEffect(() => {
    setHasChanges(hasFormChanges(formData, initialData));
  }, [formData, initialData]);

  const resolvedPreviewUrls = resolvePresignedUrls(
    dappPreviewUrls,
    presignedUrls,
  );

  const { handlePreviewFileChange, previewDimensions } = usePreviewFileHandler({
    formPreviews: formData.dappPreviews,
    existingUrls: resolvedPreviewUrls,
    onFileChange: (index, file) => {
      setFormData((previous) => {
        const nextPreviews = [...previous.dappPreviews];
        nextPreviews[index] = file;
        return { ...previous, dappPreviews: nextPreviews };
      });
    },
  });

  const handleAddPreviewSlot = () => {
    setFormData((previous) => ({
      ...previous,
      dappPreviews: [...previous.dappPreviews, null],
    }));
    setDappPreviewUrls((previous) => [...previous, null]);
  };

  const handleRemovePreviewSlot = (index: number) => {
    if (formData.dappPreviews.length <= MIN_PREVIEW_IMAGES) {
      return;
    }

    setFormData((previous) => {
      const nextPreviews = [...previous.dappPreviews];
      nextPreviews.splice(index, 1);
      return { ...previous, dappPreviews: nextPreviews };
    });

    setDappPreviewUrls((previous) => {
      const nextUrls = [...previous];
      nextUrls.splice(index, 1);
      return nextUrls;
    });
  };

  const handleSingleFileChange = useSingleFileHandler({
    onFileChange: (fieldName, file) => {
      setFormData((previous) => ({ ...previous, [fieldName]: file }));
    },
  });

  const resolvedDappIconUrl = resolvePresignedUrl(dappIconUrl, presignedUrls);
  const resolvedBannerUrl = resolvePresignedUrl(bannerUrl, presignedUrls);
  const resolvedEditorsChoiceGraphicUrl = resolvePresignedUrl(
    editorsChoiceGraphicUrl,
    presignedUrls,
  );

  const previewPanelData: DashboardPreviewData = {
    ...previewData,
    existingDappIconUrl: resolvePresignedUrl(
      previewData.existingDappIconUrl || null,
      presignedUrls,
    ),
    existingBannerUrl: resolvePresignedUrl(
      previewData.existingBannerUrl || null,
      presignedUrls,
    ),
    existingPreviewUrls: resolvePresignedUrls(
      previewData.existingPreviewUrls || [],
      presignedUrls,
    ),
  };

  const uploadFile = async (
    file: File,
    folder: 'avatars' | 'logos' | 'banners' | 'previews' | 'documents',
  ): Promise<string> => {
    return uploadPortalMediaFile({
      trpc,
      file,
      folder,
      fileType:
        folder === 'logos'
          ? 'AppIcon'
          : folder === 'banners'
            ? 'ReleaseBanner'
            : 'ReleaseScreenshot',
      mode: folder === 'previews' ? 'media' : 'image',
    });
  };

  const updateDappWithImages = async (validatedData: UpdateDappFormData) => {
    const updateStartTime = Date.now();

    try {
      if (!dappId) {
        throw new Error('No dapp ID provided');
      }

      let nextDappIconUrl: string | undefined;
      let nextBannerUrl: string | undefined;
      let nextEditorsChoiceGraphicUrl: string | undefined;
      const nextDappPreviewUrls: string[] = [];

      if (formData.dappIcon) {
        nextDappIconUrl = await uploadFile(formData.dappIcon, 'logos');
      }

      if (formData.banner) {
        nextBannerUrl = await uploadFile(formData.banner, 'banners');
      }

      if (formData.editorsChoiceGraphic) {
        nextEditorsChoiceGraphicUrl = await uploadPortalMediaFile({
          trpc,
          file: formData.editorsChoiceGraphic,
          folder: 'previews',
          fileType: 'ReleaseFeatureGraphic',
          mode: 'image',
        });
      }

      for (let index = 0; index < formData.dappPreviews.length; index += 1) {
        const preview = formData.dappPreviews[index];

        if (preview) {
          const previewUrl = await uploadFile(preview, 'previews');
          nextDappPreviewUrls.push(previewUrl);
        } else {
          const existingPreviewUrl = dappPreviewUrls[index];
          if (existingPreviewUrl) {
            nextDappPreviewUrls.push(existingPreviewUrl);
          }
        }
      }

      const updateData = buildUpdateDappPayload({
        dappId,
        initialData,
        validatedData,
      });

      if (nextDappIconUrl) {
        updateData.dappIconUrl = nextDappIconUrl;
      }
      if (nextBannerUrl) {
        updateData.bannerUrl = nextBannerUrl;
      }
      if (nextEditorsChoiceGraphicUrl) {
        updateData.editorsChoiceGraphicUrl = nextEditorsChoiceGraphicUrl;
      }
      if (nextDappPreviewUrls.length > 0) {
        updateData.dappPreviewUrls = nextDappPreviewUrls;
      }

      const result = await trpc.updateDapp.mutate(updateData);

      if (isLeft(result)) {
        throw new Error(result.left.message);
      }

      const elapsedTime = Date.now() - updateStartTime;
      const minimumSpinnerTime = 1000;
      const remainingTime = Math.max(0, minimumSpinnerTime - elapsedTime);

      if (remainingTime > 0) {
        await delay(remainingTime);
      }

      toast.success('dApp updated successfully');

      const updatedDapp = result.right;
      const updatedPreviewCount = Math.max(
        MIN_PREVIEW_IMAGES,
        updatedDapp.dappPreviewUrls?.length || 0,
      );
      const updatedAvailableCountriesMode =
        updatedDapp.availableCountriesMode === 'exclude'
          ? 'exclude'
          : 'include';
      const updatedAvailableCountries =
        updatedAvailableCountriesMode === 'exclude'
          ? normalizeCountryCodeInputs(
              updatedDapp.availableCountries ?? formData.availableCountries,
            )
          : normalizeAvailableCountries(
              updatedDapp.availableCountries ?? formData.availableCountries,
            );

      setInitialData({
        ...formData,
        availableCountriesMode: updatedAvailableCountriesMode,
        availableCountries: updatedAvailableCountries,
        dappIcon: null,
        banner: null,
        editorsChoiceGraphic: null,
        dappPreviews: Array(updatedPreviewCount).fill(null),
      });

      setFormData((previous) => ({
        ...previous,
        availableCountriesMode: updatedAvailableCountriesMode,
        availableCountries: updatedAvailableCountries,
      }));

      if (updatedDapp.dappIconUrl) {
        setDappIconUrl(updatedDapp.dappIconUrl);
      }
      if (updatedDapp.bannerUrl) {
        setBannerUrl(updatedDapp.bannerUrl);
      }
      if (updatedDapp.editorsChoiceGraphicUrl) {
        setEditorsChoiceGraphicUrl(updatedDapp.editorsChoiceGraphicUrl);
      }
      if (updatedDapp.presignedUrls) {
        setPresignedUrls(updatedDapp.presignedUrls);
      }
      if (
        updatedDapp.dappPreviewUrls &&
        updatedDapp.dappPreviewUrls.length > 0
      ) {
        setDappPreviewUrls(updatedDapp.dappPreviewUrls);
        setFormData((previous) => ({
          ...previous,
          dappPreviews: Array(updatedPreviewCount).fill(null),
        }));
      }

      return result.right;
    } catch (error) {
      captureAndReportError('Error updating dapp:', error);
      toast.error('Failed to update dApp', {
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
      });
      throw error;
    }
  };

  const handleSubmit = (
    input: TaskEither<TypedError<'InvalidFormData'>, UpdateDappFormData>,
  ): TaskEither<unknown, unknown> => {
    console.log('[handleSubmit] Called with input:', input);

    return pipe(
      input,
      chainW((validatedData) =>
        tryCatch(
          async () => {
            setIsSubmitting(true);

            try {
              const result = await updateDappWithImages(validatedData);
              setIsSubmitting(false);
              return result;
            } catch (error) {
              setIsSubmitting(false);
              throw error;
            }
          },
          (error) => {
            return error as unknown;
          },
        ),
      ),
    );
  };

  useEffect(() => {
    return () => {
      if (formData.dappIcon && dappIconUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(dappIconUrl);
      }
      if (formData.banner && bannerUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(bannerUrl);
      }
      if (
        formData.editorsChoiceGraphic &&
        editorsChoiceGraphicUrl?.startsWith('blob:')
      ) {
        URL.revokeObjectURL(editorsChoiceGraphicUrl);
      }
      dappPreviewUrls.forEach((url, index) => {
        if (url && formData.dappPreviews[index] && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [dappId]);

  return {
    isLoading,
    isSubmitting,
    isFormValid,
    hasChanges,
    isValidatingWallet: false,
    formData,
    previewDimensions,
    previewPanelData,
    dappPreviewUrls,
    resolvedDappIconUrl,
    resolvedBannerUrl,
    resolvedPreviewUrls,
    resolvedEditorsChoiceGraphicUrl,
    handleInputChange,
    handlePreviewFileChange,
    handleAddPreviewSlot,
    handleRemovePreviewSlot,
    handleSingleFileChange,
    handleSubmit,
    setIsFormValid,
    removeExistingDappIcon: () => setDappIconUrl(null),
    removeExistingBanner: () => setBannerUrl(null),
    removeExistingPreview: (index: number) => {
      setDappPreviewUrls((previous) => {
        const nextUrls = [...previous];
        nextUrls[index] = null;
        return nextUrls;
      });
    },
    removeExistingGraphic: () => setEditorsChoiceGraphicUrl(null),
  };
}
